import { supabase } from "@/integrations/supabase/client";

const STORAGE_EVENT_PREFIX = "afiliabr-supabase-store:";

export function loadCachedValue<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (raw) {
      return JSON.parse(raw) as T;
    }
  } catch {
    // ignore
  }

  return fallback;
}

export function saveCachedValue<T>(key: string, data: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(data));
    window.dispatchEvent(new CustomEvent(`${STORAGE_EVENT_PREFIX}${key}`, { detail: data }));
  } catch {
    // ignore
  }
}

export function subscribeToCachedValue<T>(key: string, fallback: T, onChange: (value: T) => void) {
  const customEventName = `${STORAGE_EVENT_PREFIX}${key}`;

  const handleCustomUpdate = (event: Event) => {
    const customEvent = event as CustomEvent<T>;
    onChange(customEvent.detail);
  };

  const handleStorageUpdate = (event: StorageEvent) => {
    if (event.key !== key) return;
    onChange(loadCachedValue<T>(key, fallback));
  };

  window.addEventListener(customEventName, handleCustomUpdate as EventListener);
  window.addEventListener("storage", handleStorageUpdate);

  return () => {
    window.removeEventListener(customEventName, handleCustomUpdate as EventListener);
    window.removeEventListener("storage", handleStorageUpdate);
  };
}

export async function fetchSupabaseCollection<TRow>(table: string, orderBy: string) {
  const { data, error } = await supabase.from(table).select("*").order(orderBy, { ascending: true });

  if (error) {
    throw error;
  }

  return (data ?? []) as TRow[];
}

export async function replaceSupabaseCollection<TRow extends Record<string, unknown>>({
  table,
  rows,
  orderBy,
  idColumn = "id",
}: {
  table: string;
  rows: TRow[];
  orderBy: string;
  idColumn?: string;
}) {
  const { data: existingRows, error: existingError } = await supabase.from(table).select(idColumn);

  if (existingError) {
    throw existingError;
  }

  const existingIds = (existingRows ?? [])
    .map((row) => row[idColumn])
    .filter((value) => value !== null && value !== undefined);

  const nextIds = rows
    .map((row) => row[idColumn])
    .filter((value) => value !== null && value !== undefined);

  const removedIds = existingIds.filter((value) => !nextIds.includes(value));

  if (removedIds.length > 0) {
    const { error: deleteError } = await supabase.from(table).delete().in(idColumn, removedIds);

    if (deleteError) {
      throw deleteError;
    }
  }

  if (rows.length > 0) {
    const { error: upsertError } = await supabase.from(table).upsert(rows, { onConflict: idColumn });

    if (upsertError) {
      throw upsertError;
    }
  }

  return fetchSupabaseCollection<TRow>(table, orderBy);
}

export async function upsertSupabaseSingleton<TRow extends Record<string, unknown>>({
  table,
  row,
}: {
  table: string;
  row: TRow;
}) {
  const { error } = await supabase.from(table).upsert(row);

  if (error) {
    throw error;
  }

  const { data, error: fetchError } = await supabase.from(table).select("*").limit(1).maybeSingle();

  if (fetchError) {
    throw fetchError;
  }

  return data as TRow | null;
}
